import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import i18n, {
  DEFAULT_LOCALE,
  SUPPORTED_LOCALES,
  type SupportedLocaleCode,
} from './index';

/**
 * Tracks the active UI locale plus how it was chosen ("auto-detected from
 * navigator" vs "user picked it"). The Registration → Start screen submits
 * this value as `preferredCulture` so the user's chosen language flows
 * straight through to the platform default in the main app.
 *
 * Persisted to localStorage under the same key i18next's LanguageDetector
 * uses, so a refresh resumes the chosen language without round-tripping
 * the API.
 */

export type LocaleSource = 'auto' | 'user';

interface LocaleState {
  locale: SupportedLocaleCode;
  source: LocaleSource;
  setLocale: (locale: SupportedLocaleCode) => void;
  resetToDetected: () => void;
}

function normalizeFromI18n(): SupportedLocaleCode {
  const raw = (i18n.resolvedLanguage ?? i18n.language ?? DEFAULT_LOCALE) as string;
  const match = SUPPORTED_LOCALES.find((l) => l.code === raw);
  if (match) return match.code;
  // Map browser short codes (es, zh, ko, en) to our regional defaults.
  const prefix = raw.split('-')[0];
  const fallback = SUPPORTED_LOCALES.find((l) => l.code.startsWith(`${prefix}-`));
  return fallback?.code ?? DEFAULT_LOCALE;
}

export const useLocaleStore = create<LocaleState>()(
  persist(
    (set) => ({
      locale: normalizeFromI18n(),
      // If LanguageDetector resolved the locale from localStorage, treat it
      // as "user" — the user picked it on a prior visit. Otherwise "auto".
      source:
        typeof window !== 'undefined' &&
        window.localStorage.getItem('invetrak.locale')
          ? 'user'
          : 'auto',
      setLocale: (locale) => {
        void i18n.changeLanguage(locale);
        set({ locale, source: 'user' });
      },
      resetToDetected: () => {
        const detected = normalizeFromI18n();
        void i18n.changeLanguage(detected);
        set({ locale: detected, source: 'auto' });
      },
    }),
    {
      name: 'invetrak.localeStore',
      // Only the locale itself goes through this persist layer — i18next's
      // own LanguageDetector handles its `invetrak.locale` key separately.
      partialize: (state) => ({ locale: state.locale, source: state.source }),
    },
  ),
);
